import { FC, useRef, useState } from 'react';
import { Typography, message } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useFelaEnhanced } from 'hooks';
import { SeoHead } from 'modules/seo';

import * as felaRules from './AssistantPage.rules';
import { AskInput } from '../AskInput';
import { AnswerCard } from '../AnswerCard';
import { AssistantAnswer } from '../../types';
import { askAssistantAuto } from '../../services/assistantApi';
import { buildRagAnswer, buildSqlAnswer } from '../../services/mapAssistantAnswer';

export const AssistantPage: FC = () => {
    const { styles } = useFelaEnhanced(felaRules);
    const intl = useIntl();
    const token = useSelector<any>(state => state?.petrus?.entities?.tokens?.accessToken?.token) as
        | string
        | undefined;
    const nextId = useRef(0);

    const [answers, setAnswers] = useState<AssistantAnswer[]>([]);
    const [loading, setLoading] = useState(false);
    const [pendingQuestion, setPendingQuestion] = useState<string>();

    const handleSubmit = async (question: string) => {
        setLoading(true);
        setPendingQuestion(question);
        try {
            const response = await askAssistantAuto({ question, token, locale: intl.locale });
            const id = `answer-${nextId.current++}`;
            const answer = response.answer_type === 'sql' ? buildSqlAnswer(response, id) : buildRagAnswer(response, id);
            setAnswers(prev => [answer, ...prev]);
        } catch (error) {
            message.error(intl.formatMessage({ id: 'assistant.error' }));
        } finally {
            setLoading(false);
            setPendingQuestion(undefined);
        }
    };

    return (
        <div className={styles.container}>
            <SeoHead title={intl.formatMessage({ id: 'page.assistant.title' })} />
            <div className={styles.header}>
                <Typography.Title level={2} className={styles.title}>
                    <FormattedMessage id="page.assistant.title" />
                </Typography.Title>
                <div className={styles.subtitle}>
                    <FormattedMessage id="page.assistant.subtitle" />
                </div>
            </div>

            <div className={styles.inputSection}>
                <AskInput
                    loading={loading}
                    showExamples={answers.length === 0 && !loading}
                    onSubmit={handleSubmit}
                />
            </div>

            <div className={styles.thread}>
                {loading && (
                    <div className={styles.threadItem}>
                        <AnswerCard loading={loading} pendingQuestion={pendingQuestion} />
                    </div>
                )}
                {answers.map(answer => (
                    <div className={styles.threadItem} key={answer.id}>
                        <AnswerCard answer={answer} loading={false} />
                    </div>
                ))}
            </div>

            {answers.length > 0 && (
                <div className={styles.disclaimer}>
                    <FormattedMessage id="page.assistant.disclaimer" />
                </div>
            )}
        </div>
    );
};
